import React, { useEffect, useRef, useState } from "react";
import { Outlet, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BuildingAPI, DashboardAPI, FloorAPI, ShapeAPI, NavigationAPI, resolveAsset } from "../../services/api";
import { isQrActive } from "../../utils/qrStatus";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { parseNames } from "../../utils/nameParser";

// SVG Icon imports for user layout 2
import { ReactComponent as ElevatorIcon } from "../../assets/icons/user2/elevator.svg";
import { ReactComponent as MagnifyingGlassIcon } from "../../assets/icons/dashboard/magnifyingglass.svg";
import { ReactComponent as StairsIcon } from "../../assets/icons/user2/stairs.svg";
import { ReactComponent as BathroomIcon } from "../../assets/icons/user2/bathroom.svg";
import { ReactComponent as BuildingIcon } from "../../assets/icons/layout1/building.svg";

const denorm = (v, scale) => (v <= 1 ? v * scale : v);

const getShapeCenter = (shape, imgW = 1000, imgH = 700) => {
  if (Array.isArray(shape.points) && shape.points.length > 0) {
    const sumX = shape.points.reduce((sum, p) => sum + denorm(p.x, imgW), 0);
    const sumY = shape.points.reduce((sum, p) => sum + denorm(p.y, imgH), 0);
    return {
      x: sumX / shape.points.length,
      y: sumY / shape.points.length,
    };
  }
  const x = denorm(shape.x || 0, imgW);
  const y = denorm(shape.y || 0, imgH);
  const w = denorm(shape.width || 0, imgW);
  const h = denorm(shape.height || 0, imgH);
  return {
    x: x + w / 2,
    y: y + h / 2,
  };
};

const shapeBelongsToFloor = (shape, floorId) => {
  const shapeFloorId = shape.floorId ?? shape.floor_id ?? shape.floor?.id;
  return shapeFloorId == null || String(shapeFloorId) === String(floorId);
};

const toSafeDbId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 && id <= 2147483647 ? id : undefined;
};

const normalizeSearchText = (value) =>
  String(value || "").trim().toLowerCase().replace(/\s+/g, "");

function extractTopRouteItems(data) {
  const raw =
    (Array.isArray(data) && data) ||
    data?.items ||
    data?.routes ||
    data?.topRoutes ||
    data?.data?.items ||
    data?.data?.routes ||
    data?.data?.topRoutes ||
    [];
  return Array.isArray(raw) ? raw : [];
}

function getRouteDestinationKeys(item) {
  const destinationName =
    item.destinationName ||
    item.toName ||
    item.destName ||
    item.roomName ||
    item.name ||
    item.destination?.name ||
    "";
  return [
    item.destinationId,
    item.toId,
    item.shapeId,
    item.destinationShapeId,
    item.destination?.id,
    destinationName && `name:${normalizeSearchText(destinationName)}`,
  ]
    .filter((value) => value != null && value !== "")
    .map(String);
}

function getDestinationKeys(dest) {
  return [
    dest.id,
    dest.destinationId,
    dest.shapeId,
    dest.name && `name:${normalizeSearchText(dest.name)}`,
  ]
    .filter((value) => value != null && value !== "")
    .map(String);
}

function normalizeFloorName(value) {
  return String(value || "")
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/floor|ชั้น|ชั้นที่/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, "");
}

function floorComparableValue(floor) {
  const level = Number(floor?.level ?? floor?.floorLevel);
  if (Number.isFinite(level)) return `level:${level}`;
  const name = normalizeFloorName(floor?.name || floor?.floorName);
  return name ? `name:${name}` : "";
}

function destinationNeedsVerticalRoute(dest, scannedFloorId, currentFloorId, floors) {
  const destFloorId = dest?.floorId || currentFloorId;
  if (String(destFloorId) === String(scannedFloorId)) return false;

  const scannedFloor = floors.find((floor) => String(floor.id) === String(scannedFloorId));
  const scannedComparable = floorComparableValue(scannedFloor);
  const destComparable = floorComparableValue(dest);
  if (scannedComparable && destComparable) return scannedComparable !== destComparable;

  return String(destFloorId) !== String(scannedFloorId);
}

export default function UserLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const buildingId = searchParams.get("buildingId");
  const floorId = searchParams.get("floorId");
  // ชั้นที่สแกน QR จริง (persist ไว้เสมอแม้ผู้ใช้สลับชั้น)
  const scannedFloorId = searchParams.get("scannedFloorId") || searchParams.get("floorId");

  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [openBuilding, setOpenBuilding] = useState(false);
  const [openFloor, setOpenFloor] = useState(false);

  const [destinations, setDestinations] = useState([]);
  const [filteredDestinations, setFilteredDestinations] = useState([]);
  const [popularDestinationScores, setPopularDestinationScores] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResultsOpen, setSearchResultsOpen] = useState(false);
  const [selectedSearchDestination, setSelectedSearchDestination] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null); // 'stair' | 'elevator' | 'restroom' | null
  const [routePreference, setRoutePreference] = useState("");
  const searchPanelRef = useRef(null);


  // ป้องกันการซูมและดับเบิ้ลแทปในหน้าเว็บ (ยกเว้นบนแผนที่ Leaflet)
  useEffect(() => {
    const preventPinchZoom = (e) => {
      if (e.touches.length > 1) {
        const isMap = e.target.closest(".leaflet-container");
        if (!isMap) {
          e.preventDefault();
        }
      }
    };

    let lastTouchEnd = 0;
    const preventDoubleTapZoom = (e) => {
      const now = new Date().getTime();
      if (now - lastTouchEnd <= 300) {
        const isMap = e.target.closest(".leaflet-container");
        if (!isMap) {
          e.preventDefault();
        }
      }
      lastTouchEnd = now;
    };

    document.addEventListener("touchstart", preventPinchZoom, { passive: false });
    document.addEventListener("touchend", preventDoubleTapZoom, { passive: false });

    return () => {
      document.removeEventListener("touchstart", preventPinchZoom);
      document.removeEventListener("touchend", preventDoubleTapZoom);
    };
  }, []);

  // ชุดคำค้น (ไทย+อังกฤษ) สำหรับ shortcut filter หมวด บันได/ลิฟต์/ห้องน้ำ
  const CATEGORY_KEYWORDS = {
    stair: ["stair", "บันได"],
    elevator: ["elevator", "lift", "ลิฟต์", "ลิฟท์", "ลิฟ"],
    restroom: ["restroom", "toilet", "wc", "ห้องน้ำ", "สุขา"],
  };
  /*
  const CATEGORY_LABEL = {
    stair: "บันได",
    elevator: "ลิฟต์",
    restroom: "ห้องน้ำ",
  };
  */

  const selectedMapIcons = new Set(
    String(searchParams.get("mapIcons") || "")
      .split(",")
      .filter(Boolean)
  );

  useEffect(() => {
    if (!searchResultsOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (searchPanelRef.current?.contains(event.target)) return;
      setSearchResultsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSearchResultsOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [searchResultsOpen]);

  useEffect(() => {
    let active = true;
    DashboardAPI.topRoutesAnalytics({ period: "30d", limit: 100 })
      .then((data) => {
        if (!active) return;
        const scores = {};
        extractTopRouteItems(data).forEach((item, index) => {
          const score = Number(
            item.scans ?? item.count ?? item.total ?? item.totalScans ?? item.users ?? 0
          ) || Math.max(1, 100 - index);
          getRouteDestinationKeys(item).forEach((key) => {
            scores[key] = Math.max(scores[key] || 0, score);
          });
        });
        setPopularDestinationScores(scores);
      })
      .catch(() => {
        if (active) setPopularDestinationScores({});
      });

    return () => {
      active = false;
    };
  }, []);

  const getDestinationPopularity = (dest) =>
    Math.max(
      0,
      ...getDestinationKeys(dest).map((key) => Number(popularDestinationScores[key]) || 0)
    );

  const sortDestinationsForDropdown = (list, { limit = 20 } = {}) =>
    [...list]
      .sort((a, b) => {
        const popularityDiff = getDestinationPopularity(b) - getDestinationPopularity(a);
        if (popularityDiff) return popularityDiff;
        const currentFloorDiff =
          (String(b.floorId) === String(floorId) ? 1 : 0) -
          (String(a.floorId) === String(floorId) ? 1 : 0);
        if (currentFloorDiff) return currentFloorDiff;
        return String(a.name || "").localeCompare(String(b.name || ""), "th");
      })
      .slice(0, limit);

  const toggleMapIcon = (key) => {
    const next = new Set(selectedMapIcons);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }

    setSearchParams(
      (prev) => {
        const value = [...next].join(",");
        if (value) {
          prev.set("mapIcons", value);
        } else {
          prev.delete("mapIcons");
        }
        return prev;
      },
      { replace: true }
    );
  };

  // Load destinations from every floor across ALL buildings เพื่อให้ค้นหาปลายทางข้ามตึกได้
  useEffect(() => {
    if (!buildings.length) {
      setDestinations([]);
      setFilteredDestinations([]);
      return;
    }

    let cancelled = false;

    async function loadAllDestinations() {
      const perBuilding = await Promise.all(
        buildings.map(async (building) => {
          // ใช้ floors ที่โหลดมาแล้วถ้าเป็นตึกปัจจุบัน, ไม่งั้น fetch
          let buildingFloors = [];
          if (String(building.id) === String(buildingId) && floors.length > 0) {
            buildingFloors = floors;
          } else {
            buildingFloors = await FloorAPI.listByBuilding(building.id)
              .then((d) => (Array.isArray(d) ? d : d?.items || []))
              .catch(() => []);
          }

          const floorGroups = await Promise.all(
            buildingFloors.map((floor) =>
              Promise.all([
                ShapeAPI.listByFloor(floor.id).catch(() => []),
                NavigationAPI.getFloor(floor.id).catch(() => null),
              ]).then(([shapesData, floorData]) => {
                const shapeList = (Array.isArray(shapesData) ? shapesData : []).filter((shape) =>
                  shapeBelongsToFloor(shape, floor.id)
                );
                const imgW = floorData?.imageWidth || 1000;
                const imgH = floorData?.imageHeight || 700;

                return shapeList
                  .filter((s) => !s.hidden && s.name)
                  .map((s) => {
                    const center = getShapeCenter(s, imgW, imgH);
                    return {
                      id: s.id,
                      destinationId: s.destinationId,
                      shapeId: s.id,
                      name: s.name,
                      category: s.category || "room",
                      description: s.description || "",
                      x: Math.round(center.x),
                      y: Math.round(center.y),
                      imageUrl: s.imageUrl || s.image || s.img || "",
                      buildingId: building.id,
                      buildingName: building.name || `${t("user.layout.building", "อาคาร")} ${building.id}`,
                      floorId: floor.id,
                      floorLevel: floor.level,
                      floorName: floor.name || `${t("user.layout.floor")} ${floor.level || ""}`,
                    };
                  });
              })
            )
          );
          return floorGroups.flat();
        })
      );

      if (!cancelled) setDestinations(perBuilding.flat());
    }

    loadAllDestinations().catch(() => {
      if (!cancelled) setDestinations([]);
    });

    return () => {
      cancelled = true;
    };
  }, [buildings, buildingId, floors, t]);

  // Filter destinations เมื่อ searchQuery หรือ categoryFilter เปลี่ยน
  useEffect(() => {
    // โหมด category shortcut: filter หมวดบนชั้นปัจจุบัน
    if (categoryFilter) {
      const keys = CATEGORY_KEYWORDS[categoryFilter] || [];
      const matches = destinations.filter((d) => {
        // จำกัดเฉพาะชั้นปัจจุบัน (เพราะ user มักหาในชั้นที่อยู่)
        if (floorId && String(d.floorId) !== String(floorId)) return false;
        const hay = `${d.name || ""} ${d.category || ""} ${d.description || ""}`.toLowerCase();
        return keys.some((k) => hay.includes(k.toLowerCase()));
      });
      setFilteredDestinations(sortDestinationsForDropdown(matches, { limit: 10 }));
      return;
    }
    if (!searchQuery.trim()) {
      setFilteredDestinations(sortDestinationsForDropdown(destinations, { limit: 10 }));
      return;
    }
    const q = searchQuery.toLowerCase().trim();
    const filtered = destinations.filter((d) => {
      const { all } = parseNames(d.name);
      const matchesName = all.some((n) => n.toLowerCase().includes(q));
      const matchesDesc = (d.description || "").toLowerCase().includes(q);
      const matchesCat = (d.category || "").toLowerCase().includes(q);
      return matchesName || matchesDesc || matchesCat;
    });
    setFilteredDestinations(sortDestinationsForDropdown(filtered, { limit: 10 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, destinations, categoryFilter, floorId, popularDestinationScores]);

  // Load buildings
  useEffect(() => {
    BuildingAPI.list()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setBuildings(list);
        if (!buildingId && list.length > 0) {
          setSearchParams({ buildingId: String(list[0].id) }, { replace: true });
        }
      })
      .catch(() => setBuildings([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load floors of selected building
  useEffect(() => {
    if (!buildingId) {
      setFloors([]);
      return;
    }
    FloorAPI.listByBuilding(buildingId)
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setFloors(list);
        const currentFloorIdInUrl = new URLSearchParams(window.location.search).get("floorId");
        const exists = list.some((f) => String(f.id) === String(currentFloorIdInUrl));
        if (list.length > 0 && (!currentFloorIdInUrl || !exists)) {
          setSearchParams(
            { buildingId, floorId: String(list[0].id) },
            { replace: true }
          );
        }
      })
      .catch(() => setFloors([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  const currentBuilding = buildings.find((b) => String(b.id) === String(buildingId));
  const currentFloor = floors.find((f) => String(f.id) === String(floorId));

  // eslint-disable-next-line no-unused-vars
  const pickBuilding = (id) => {
    setOpenBuilding(false);
    setFloors([]);
    setSearchParams({ buildingId: String(id) }, { replace: true });
  };
  const pickFloor = (id) => {
    setOpenFloor(false);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams();
        if (buildingId) next.set("buildingId", String(buildingId));
        next.set("floorId", String(id));
        // คงค่า currentQrId + scannedFloorId ไว้เพื่อให้แสดงห้องทุกชั้นหลัง scan
        const qrId = prev.get("currentQrId");
        const sfId = prev.get("scannedFloorId");
        if (qrId) next.set("currentQrId", qrId);
        if (sfId) next.set("scannedFloorId", sfId);
        return next;
      },
      { replace: true }
    );
  };

  const startNavigationToDestination = async (dest) => {
    setSearchQuery("");
    setCategoryFilter(null);
    setSearchResultsOpen(false);
    setSelectedSearchDestination(null);

    const currentQrId = searchParams.get("currentQrId");
    if (!currentQrId || !buildingId) {
      alert(t("user.mainPage.scanQrFirst"));
      return;
    }

    // ตรวจว่านำทางข้ามชั้นจริงหรือไม่ (ข้ามตึกชั้น 1 ไปชั้น 1 ไม่ต้องถามลิฟต์/บันได)
    const isCrossFloor = destinationNeedsVerticalRoute(dest, scannedFloorId, floorId, floors);

    const preference = isCrossFloor ? routePreference : "elevator";
    await doSearchNavigation(dest, preference);
    setRoutePreference(""); // Reset preference after navigation
  };

  const doSearchNavigation = async (dest, mode) => {
    const currentQrId = searchParams.get("currentQrId");
    try {
      // ดึง QR position จากชั้นที่สแกนจริง (scannedFloorId) ไม่ใช่ชั้นที่กำลังดูอยู่
      const floorData = await NavigationAPI.getFloor(scannedFloorId);
      const qr = (floorData?.qr || []).find(
        (point) => String(point.id) === String(currentQrId) && isQrActive(point)
      );
      if (!qr) {
        alert(t("user.mainPage.qrPositionNotFound", "ไม่พบตำแหน่ง QR"));
        return;
      }

      // log route
      const routeLogPayload = {
        floorId: Number(scannedFloorId),
        startQrId: Number(qr.id),
        startName: qr.name,
        destinationName: parseNames(dest.name, true).primary,
        userSessionId: getSessionId(),
      };
      const shapeId = toSafeDbId(dest.shapeId ?? dest.id);
      if (shapeId) routeLogPayload.shapeId = shapeId;
      NavigationAPI.logRoute(routeLogPayload).catch(() => { });

      const destFloorId = dest.floorId || floorId;
      const params = new URLSearchParams({
        buildingId: String(buildingId),
        floorId: String(scannedFloorId),          // ชั้นเริ่มต้น (จาก QR)
        fromQrId: String(currentQrId),
        toId: String(dest.id),
        startX: String(Math.round(Number(qr.x))),
        startY: String(Math.round(Number(qr.y))),
        endX: String(Math.round(Number(dest.x))),
        endY: String(Math.round(Number(dest.y))),
        targetFloorId: String(destFloorId),        // ชั้นปลายทาง
        targetBuildingId: String(dest.buildingId || buildingId),
        destName: String(dest.name || t("user.mainPage.destination")),
        routeMode: mode || "elevator",
      });
      navigate(`/user/usernavigation?${params.toString()}`);
    } catch {
      alert(t("user.navigation.cannotCalculate", "ไม่สามารถคำนวณเส้นทางได้"));
    }
  };

  const searchHasIntent = Boolean(searchResultsOpen || searchQuery.trim() || categoryFilter);
  const searchDropdownTitle = searchQuery.trim()
    ? t("user.menuList.searchResults")
    : "ปลายทางยอดนิยม";

  const clearSearch = () => {
    setSearchQuery("");
    setCategoryFilter(null);
    setSearchResultsOpen(false);
    setSelectedSearchDestination(null);
    setRoutePreference(""); // Reset preference
    // ลบ destId ออกจาก URL เพื่อให้แผนที่เปิดแสดง marker ทั้งหมดตามปกติ
    setSearchParams((prev) => {
      prev.delete("destId");
      prev.delete("destFloorId");
      return prev;
    }, { replace: true });
  };

  const destinationImage = (dest) => resolveAsset(dest?.imageUrl || dest?.img || "");

  const destinationMeta = (dest) =>
    [dest?.floorName, currentBuilding?.name].filter(Boolean).join(" - ");

  const needsRoutePreference = (dest) => {
    if (!dest) return false;
    const currentQrId = searchParams.get("currentQrId");
    if (!currentQrId) return false;
    return destinationNeedsVerticalRoute(dest, scannedFloorId, floorId, floors);
  };

  const isUser2 = location.pathname.startsWith("/user2");

  // Render User Layout 2 UI if route is /user2
  if (isUser2) {
    return (
      <div className="w-full h-screen">
        <div className="mt-6 items-center gap-2 w-full">
          <div className="flex items-stretch gap-2 px-4">
            {/* SEARCH */}
            <div ref={searchPanelRef} className="relative flex flex-1 min-w-0 items-center rounded-lg border border-slate-300 shadow-md bg-white py-3">
              <MagnifyingGlassIcon className="mr-2 ml-2 shrink-0 w-6 h-6 text-slate-500" />
              <input
                value={searchQuery}
                onChange={(e) => {
                  setCategoryFilter(null);
                  setSearchResultsOpen(true);
                  setSearchQuery(e.target.value);
                }}
                onFocus={() => setSearchResultsOpen(true)}
                disabled={!floorId}
                className="w-full min-w-0 bg-transparent text-sm sm:text-sm text-slate-700 outline-none placeholder:text-slate-500 disabled:opacity-60"
                placeholder={floorId ? t("user.layout.searchPlaceholder2") : t("user.layout.searchFloorFirst2")}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="text-slate-400 hover:text-slate-600 text-xs px-2"
                >
                  ✕
                </button>
              )}

              {/* SEARCH RESULTS DROPDOWN FOR LAYOUT 2 */}
              {searchResultsOpen && filteredDestinations.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-slate-200 max-h-[220px] overflow-y-auto z-50">
                  <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-100">
                    <div className="text-[11px] font-bold text-slate-400">
                      {searchDropdownTitle}
                    </div>
                    <button
                      type="button"
                      onClick={() => setSearchResultsOpen(false)}
                      className="grid h-6 w-6 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                      aria-label="ปิดรายการ"
                    >
                      ×
                    </button>
                  </div>
                  {filteredDestinations.map((dest) => (
                    <button
                      key={dest.id}
                      onClick={() => startNavigationToDestination(dest)}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        {(() => {
                          const { primary, aliases } = parseNames(dest.name);
                          return (
                            <>
                              <div className="text-sm font-bold text-slate-900 truncate">
                                {primary}
                              </div>
                              {aliases.length > 0 && (
                                <div className="text-[11px] text-slate-400 truncate">
                                  ชื่ออื่น: {aliases.join(", ")}
                                </div>
                              )}
                            </>
                          );
                        })()}
                        <div className="text-[10px] text-blue-600 font-semibold uppercase">
                          {dest.category}
                          {dest.floorName ? ` • ${dest.floorName}` : ""}
                        </div>
                        {dest.buildingId && String(dest.buildingId) !== String(buildingId) && (
                          <div className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                            🏢 {dest.buildingName} (ข้ามตึก)
                          </div>
                        )}
                        {dest.description && (
                          <div className="text-[11px] text-slate-500 truncate mt-0.5">
                            {dest.description}
                          </div>
                        )}
                      </div>
                      <span className="text-slate-400 text-[10px] shrink-0 ml-2">📍 เล็ง</span>
                    </button>
                  ))}
                </div>
              )}

              {searchResultsOpen && searchQuery.trim() && filteredDestinations.length === 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-slate-200 py-4 text-center text-xs text-slate-500 z-50">
                  {t("user.layout.notFound2")}
                </div>
              )}
            </div>

            {/* LANGUAGE BUTTON */}
            <LanguageSwitcher variant="square" />
          </div>

          {/* QUICK FILTER BUTTONS */}
          <div className="justify-between flex mt-5">
            <button
              onClick={() => toggleMapIcon("stair")}
              disabled={!floorId}
              className={`flex flex-1 justify-center items-center mr-3 ml-4 rounded-xl border py-2 disabled:opacity-50 ${selectedMapIcons.has("stair")
                ? "bg-blue-600 border-blue-600 text-white"
                : "bg-white border-slate-400 text-slate-700 hover:bg-blue-600 hover:text-white"
                }`}
            >
              <StairsIcon className={`w-5 h-5 mr-1 ml-1 ${selectedMapIcons.has("stair") ? "text-white" : "text-orange-500"}`} />
              <span className="text-base font-medium ml-1 mr-1 p-1">{t("user.layout.stairs")}</span>
            </button>

            <button
              onClick={() => toggleMapIcon("elevator")}
              disabled={!floorId}
              className={`flex flex-1 justify-center items-center mr-4 rounded-xl border py-2 disabled:opacity-50 ${selectedMapIcons.has("elevator")
                ? "bg-blue-600 border-blue-600 text-white"
                : "bg-white border-slate-400 text-slate-700 hover:bg-blue-600 hover:text-white"
                }`}
            >
              <ElevatorIcon className={`w-5 h-5 ml-1 mr-1 ${selectedMapIcons.has("elevator") ? "text-white" : "text-green-600"}`} />
              <span className=" text-base font-medium ml-1 mr-1 p-1">{t("user.layout.elevator")}</span>
            </button>
          </div>

        </div>

        {/* CONTENT */}
        <main className="overflow-auto">
          <Outlet />
        </main>
      </div>
    );
  }

  // Render standard User Layout 1 UI otherwise
  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* CONTENT */}
      <main className="absolute inset-0 z-0">
        <Outlet />
      </main>

      {/* <a
        href="/login"
        className="absolute top-2 right-2 z-30 px-2 py-1 rounded text-[10px] font-semibold text-slate-500 hover:text-slate-800"
      >
        {t("user.layout.staffLink")}
      </a> */}

      <div className="fixed top-0 left-0 w-full z-20">
        <div className="mt-3 items-center w-full">
          <div className="flex">
            {/* BUILDING SELECTOR */}
            {/* <div className="relative flex-1 min-w-0">
              <button
                onClick={() => {
                  setOpenBuilding((v) => !v);
                  setOpenFloor(false);
                }}
                className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-300 shadow-md bg-white px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="text-left min-w-0">
                    <div className="text-[9px] font-semibold text-slate-400 uppercase leading-none">{t("user.layout.building")}</div>
                    <div className="text-xs sm:text-sm font-semibold text-slate-800 mt-1 truncate">
                      {currentBuilding?.name || (buildings.length === 0 ? t("user.layout.noBuilding") : t("user.layout.selectBuilding"))}
                    </div>
                  </div>
                </div>
                <span className="text-slate-400 shrink-0 text-sm">▾</span>
              </button>
              {openBuilding && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 max-h-[60vh] overflow-y-auto z-30">
                  {buildings.length === 0 && (
                    <div className="px-4 py-4 text-center text-xs text-slate-500">
                      {t("user.layout.noBuildingInSystem")}
                    </div>
                  )}
                  {buildings.map((b) => (
                    <button
                      key={b.id}
                      onClick={() => pickBuilding(b.id)}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 ${
                        String(b.id) === String(buildingId) ? "bg-blue-50" : ""
                      }`}
                    >
                      <div className="text-sm font-bold text-slate-900">{b.name}</div>
                      {b.address && (
                        <div className="text-[11px] text-slate-500 truncate">{b.address}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div> */}

            {/* FLOOR SELECTOR */}
            {/* <div className="relative w-[110px] shrink-0">
              <button
                onClick={() => {
                  setOpenFloor((v) => !v);
                  setOpenBuilding(false);
                }}
                disabled={floors.length === 0}
                className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-300 shadow-md bg-white px-3 py-2 disabled:opacity-50"
              >
                <div className="text-left min-w-0">
                  <div className="text-[9px] font-semibold text-slate-400 uppercase leading-none">{t("user.layout.floor")}</div>
                  <div className="text-xs sm:text-sm font-semibold text-slate-800 mt-1 truncate">
                    {currentFloor
                      ? currentFloor.name || `${t("user.layout.floor")} ${currentFloor.level}`
                      : "-"}
                  </div>
                </div>
                <span className="text-slate-400 shrink-0 text-sm">▾</span>
              </button>
              {openFloor && floors.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 max-h-[50vh] overflow-y-auto z-30">
                  {[...floors]
                    .sort((a, b) => (b.level || 0) - (a.level || 0))
                    .map((f) => (
                      <button
                        key={f.id}
                        onClick={() => pickFloor(f.id)}
                        className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 ${
                          String(f.id) === String(floorId) ? "bg-blue-50" : ""
                        }`}
                      >
                        <div className="text-sm font-bold text-slate-900">
                          {f.name || `ชั้น ${f.level}`}
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </div> */}
          </div>

          {/* SEARCH INPUT */}
          <div ref={searchPanelRef} className="relative px-3 mr-3 mt-2">
            <div className="flex items-stretch gap-2">
              <div className="flex flex-1 items-center rounded-lg border border-slate-300 shadow-md bg-white py-3 px-3">
                <MagnifyingGlassIcon className="mr-2 shrink-0 w-4 h-4 text-slate-400" />
                <input
                  value={searchQuery}
                  onChange={(e) => {
                    setCategoryFilter(null);
                    setSearchResultsOpen(true);
                    setSearchQuery(e.target.value);
                  }}
                  onFocus={() => setSearchResultsOpen(true)}
                  disabled={!floorId}
                  className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400 disabled:opacity-60"
                  placeholder={floorId ? t("user.layout.searchPlaceholder") : t("user.layout.searchFloorFirst")}
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="text-slate-400 hover:text-slate-600 text-xs px-1"
                  >
                    ✕
                  </button>
                )}
              </div>
              {/* FLOOR SELECTOR */}
              <div className="relative shrink-0">
                <button
                  onClick={() => {
                    setOpenFloor((v) => !v);
                    setOpenBuilding(false);
                  }}
                  disabled={floors.length === 0}
                  className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-300 shadow-md bg-white px-3 py-2 disabled:opacity-50"
                >
                  <div className="text-left min-w-0">
                    <div className="text-[9px] font-semibold text-slate-400 uppercase leading-none">{t("user.layout.floor")}</div>
                    <div className="text-xs sm:text-sm font-semibold text-slate-800 mt-1 truncate">
                      {currentFloor
                        ? currentFloor.name || `${t("user.layout.floor")} ${currentFloor.level}`
                        : "-"}
                    </div>
                  </div>
                  <span className="text-slate-400 shrink-0 text-sm">▾</span>
                </button>
                {openFloor && floors.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 max-h-[50vh] overflow-y-auto z-30">
                    {[...floors]
                      .sort((a, b) => (b.level || 0) - (a.level || 0))
                      .map((f) => (
                        <button
                          key={f.id}
                          onClick={() => pickFloor(f.id)}
                          className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 ${String(f.id) === String(floorId) ? "bg-blue-50" : ""
                            }`}
                        >
                          <div className="text-sm font-bold text-slate-900">
                            {f.name || `ชั้น ${f.level}`}
                          </div>
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <LanguageSwitcher variant="square" />
            </div>

            {searchResultsOpen && searchHasIntent && (
              <div className="fixed inset-0 z-[200] bg-white flex flex-col">
                {/* Header row — search input + close */}
                <div className="flex items-center gap-2 px-3 pt-3 pb-2 border-b border-slate-100">
                  <div className="flex flex-1 items-center rounded-lg border border-slate-300 bg-white py-2.5 px-3 shadow-sm">
                    <MagnifyingGlassIcon className="mr-2 shrink-0 w-4 h-4 text-slate-400" />
                    <input
                      autoFocus
                      value={searchQuery}
                      onChange={(e) => {
                        setCategoryFilter(null);
                        setSearchQuery(e.target.value);
                      }}
                      className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
                      placeholder={floorId ? t("user.layout.searchPlaceholder") : t("user.layout.searchFloorFirst")}
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="text-slate-400 hover:text-slate-600 text-xs px-1"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchResultsOpen(false);
                      setSearchQuery("");
                      setCategoryFilter(null);
                    }}
                    className="shrink-0 text-sm font-semibold text-slate-500 hover:text-slate-800 px-2 py-2"
                  >
                    ยกเลิก
                  </button>
                </div>

                {/* Results title */}
                <div className="px-4 pt-3 pb-1">
                  <div className="text-sm font-semibold text-slate-400">
                    {searchDropdownTitle}
                  </div>
                </div>

                {/* Results list */}
                <div className="flex-1 overflow-y-auto px-4 pb-8">
                  {filteredDestinations.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {filteredDestinations.map((dest) => {
                        const image = destinationImage(dest);
                        return (
                          <button
                            key={`${dest.floorId}-${dest.id}`}
                            onClick={() => {
                              setSelectedSearchDestination(dest);
                              setSearchResultsOpen(false);
                              setSearchQuery("");
                              // ตั้ง destId ใน URL เพื่อให้แผนที่ซ่อน marker ทั้งหมด แสดงเฉพาะปลายทาง
                              setSearchParams((prev) => {
                                prev.set("destId", String(dest.id));
                                if (dest.floorId) prev.set("destFloorId", String(dest.floorId));
                                else prev.delete("destFloorId");
                                if (dest.buildingId) prev.set("destBuildingId", String(dest.buildingId));
                                else prev.delete("destBuildingId");
                                return prev;
                              }, { replace: true });
                            }}
                            className="w-full py-3 text-left flex items-center gap-3 bg-white hover:bg-slate-50"
                          >
                            <div className="w-16 h-14 shrink-0 rounded-lg overflow-hidden bg-slate-100 border border-slate-100 flex items-center justify-center">
                              {image ? (
                                <img
                                  src={image}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <BuildingIcon className="w-6 h-6 text-slate-300" />
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              {(() => {
                                const { primary, aliases } = parseNames(dest.name);
                                return (
                                  <>
                                    <div className="font-bold text-sm text-slate-900 truncate">
                                      {primary}
                                    </div>
                                    {aliases.length > 0 && (
                                      <div className="text-xs text-slate-400 truncate mt-0.5">
                                        ชื่ออื่น: {aliases.join(", ")}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                              <div className="text-xs text-slate-500 truncate mt-1">
                                {destinationMeta(dest) || dest.category}
                              </div>
                              {dest.buildingId && String(dest.buildingId) !== String(buildingId) && (
                                <div className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                                  🏢 {dest.buildingName} (ข้ามตึก)
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-12 text-center text-sm text-slate-400">
                      {t("user.layout.notFound")}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* SEARCH QUICK FILTERS */}
          <div className="flex justify-between gap-2 px-4 mt-2">
            <button
              onClick={() => toggleMapIcon("stair")}
              disabled={!floorId}
              className={`flex flex-1 items-center justify-center rounded-xl shadow-md py-2 text-sm font-semibold transition disabled:opacity-50 ${selectedMapIcons.has("stair")
                ? "bg-slate-950 text-white"
                : "bg-slate-300 text-slate-500"
                }`}
            >
              <StairsIcon className="w-5 h-5 mr-1 text-orange-500" />
              <span>{t("user.layout.stairs")}</span>
            </button>

            <button
              onClick={() => toggleMapIcon("elevator")}
              disabled={!floorId}
              className={`flex flex-1 items-center justify-center rounded-xl shadow-md py-2 text-sm font-semibold transition disabled:opacity-50 ${selectedMapIcons.has("elevator")
                ? "bg-slate-950 text-white"
                : "bg-slate-300 text-slate-500"
                }`}
            >
              <ElevatorIcon className="w-5 h-5 mr-1 text-green-500" />
              <span>{t("user.layout.elevator")}</span>
            </button>

            <button
              onClick={() => toggleMapIcon("restroom")}
              disabled={!floorId}
              className={`flex flex-1 items-center justify-center rounded-xl shadow-md py-2 text-sm font-semibold transition disabled:opacity-50 ${selectedMapIcons.has("restroom")
                ? "bg-slate-950 text-white"
                : "bg-slate-300 text-slate-500"
                }`}
            >
              <BathroomIcon className="w-5 h-5 mr-1 text-slate-500" />
              <span>{t("user.layout.restroom")}</span>
            </button>
          </div>

          {selectedSearchDestination && (
            <div className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl bg-white shadow-[0_-8px_24px_rgba(15,23,42,0.14)] border border-slate-200 p-2">
              <div className="mx-auto my-2 h-1 w-12 rounded-full bg-slate-300" />

              {destinationImage(selectedSearchDestination) ? (
                <img
                  src={destinationImage(selectedSearchDestination)}
                  alt=""
                  className="w-full h-28 rounded-lg object-cover mb-4"
                />
              ) : (
                <div className="w-full h-28 rounded-lg bg-slate-100 border border-slate-100 flex items-center justify-center mb-4">
                  <BuildingIcon className="w-9 h-9 text-slate-300" />
                </div>
              )}

              {(() => {
                const { primary, aliases } = parseNames(selectedSearchDestination.name);
                return (
                  <div className="px-2">
                    <div className="font-bold text-base text-slate-900 leading-snug">
                      {primary}
                    </div>
                    {aliases.length > 0 && (
                      <div className="text-xs text-slate-500 font-normal mt-0.5">
                        ชื่ออื่น: {aliases.join(", ")}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="mt-2 px-2 flex items-center justify-between gap-3">
                <div className="min-w-0 flex items-center gap-2">
                  {selectedSearchDestination.floorName && (
                    <span className="shrink-0 rounded-md bg-blue-50 px-2 py-1 text-xs font-bold text-blue-600">
                      {selectedSearchDestination.floorName}
                    </span>
                  )}
                  <span className="truncate text-xs text-slate-500">
                    {currentBuilding?.name || selectedSearchDestination.category}
                  </span>
                </div>
              </div>

              <div className="mt-4 px-2">
                <div className="text-sm font-bold text-slate-900">
                  {t("user.menuList.description")}
                </div>
                <div className="mt-1 text-sm text-slate-600 leading-relaxed">
                  {selectedSearchDestination.description || destinationMeta(selectedSearchDestination) || selectedSearchDestination.category}
                </div>
              </div>

              {needsRoutePreference(selectedSearchDestination) && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setRoutePreference("stair")}
                    className={`h-11 rounded-lg border text-sm font-bold flex items-center justify-center gap-2 ${routePreference === "stair"
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-slate-200 bg-white text-slate-700"
                      }`}
                  >
                    <StairsIcon className={`h-5 w-5 ${routePreference === "stair" ? "text-white" : "text-slate-600"}`} />
                    {t("user.layout.stairs")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRoutePreference("elevator")}
                    className={`h-11 rounded-lg border text-sm font-bold flex items-center justify-center gap-2 ${routePreference === "elevator"
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-slate-200 bg-white text-slate-700"
                      }`}
                  >
                    <ElevatorIcon className={`h-5 w-5 ${routePreference === "elevator" ? "text-white" : "text-slate-700"}`} />
                    {t("user.layout.elevator")}
                  </button>
                </div>
              )}

              <button
                onClick={() => startNavigationToDestination(selectedSearchDestination)}
                className="mt-2 w-full h-12 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-base font-bold disabled:bg-slate-300"
                disabled={needsRoutePreference(selectedSearchDestination) && !routePreference}
              >
                <span className="mr-2">⚑</span>
                {t("user.menuList.startNavigation")}
              </button>
            </div>
          )}

        </div>
      </div>

    </div>
  );
}

function getSessionId() {
  const KEY = "indoor_nav_session_id";
  let sid = localStorage.getItem(KEY);
  if (!sid) {
    sid =
      (crypto?.randomUUID && crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(KEY, sid);
  }
  return sid;
}
